import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useUpdateScheduleEntry, useUpsertBreak } from '../../../entities/schedule';
import { UpsertAppointmentModal, useUpsertAppointment } from '../../../features/appointment-upsert';
import {
  applyDraftDrag,
  draftLayout,
  draftToDefaultSlot,
  slotToDraft,
  withSlotDay,
  type DispositionMode,
  type PlanningDraftController,
  type RescheduleSource,
} from '../../../features/disposition-planning';
import { UpsertTaskModal, useUpsertTask } from '../../../features/task-upsert';
import { useAuthStore } from '../../../features/auth';
import {
  CalendarOrderList,
  CalendarOrderRow,
  CalendarOrderToolbar,
  useCalendarRows,
  type CalendarOrderEditor,
} from '../../../features/technician-calendar-order';
import { getBlockColors } from '../model/blockColors';
import {
  BOARD_WIDTH,
  TECHNICIAN_COL_WIDTH,
  detectDragCollision,
  isOutsideShift,
  sameSlot,
  slotDayIso,
  type ScheduleOverrides,
  type ScheduleSlot,
  type SegmentHandle,
} from '../../../shared/lib/schedule';
import { routes } from '../../../shared/config/routes';
import { DOCKED_PANEL_WIDTH } from '../../../shared/lib/dockedPanel';
import { useScheduleConflictFlow } from '../../../shared/hooks/useScheduleConflictFlow';
import { ScheduleConflictDialogs } from '../../../shared/ui/feedback/ScheduleConflictDialogs/ScheduleConflictDialogs';
import { BOARD_LAYER } from '../model/boardLayers';
import { technicianTrades } from '../model/technicianTrades';
import { orderByTrade } from '../model/tradeMatch';
import { useGewerkLabels } from '../model/useGewerkLabels';
import type { DragPreview, PlanningDraft } from '../model/boardPreview';
import { buildSchedulePatch, type DragHandle, type DragOutcome } from '../model/buildSchedulePatch';
import { DRAFT_KEY, blockSubject, draftSubject, type DragSubject } from '../model/dragSubject';
import { resolveDragPolicy, type DragContext } from '../model/dragPolicy';
import { buildPreviewLayout } from '../model/previewLayout';
import { ROW_TECHNICIAN_ATTR, useBlockDrag } from '../model/useBlockDrag';
import { useDispositionBoard } from '../model/useDispositionBoard';
import type { BoardBlock } from '../model/boardTypes';
import type { DispositionDay } from '../model/useDispositionDay';
import { FOCUS_TECHNICIAN_PARAM } from '../model/useDispositionWeek';
import { BoardCurrentTimeLine } from './BoardCurrentTimeLine';
import { BoardMessage } from './BoardMessage';
import { BoardRow } from './BoardRow';
import { BoardSkeleton } from './BoardSkeleton';
import { BoardTimeAxis } from './BoardTimeAxis';

interface Props {
  /**
   * From `useDispositionDay()`. The page owns the hook so it can render `DispositionDayNav` into
   * `PageLayout`'s `headerChildren`; the widget only reads the day off it.
   */
  day: DispositionDay;
  /**
   * The planning state — mode, the entry being moved, the draft — is owned by the page, because the
   * planning bar and the draft have to outlive this widget: switching to Woche unmounts it, and
   * neither may be lost on the way. The widget draws and edits what it is handed.
   */
  plan: DispositionMode;
  source: RescheduleSource | null;
  planning: PlanningDraftController;
  /**
   * A form saved while planning — the page drops the draft and leaves the mode. The widget only
   * says so; it has no business knowing what "done" means for the bar.
   */
  onPlanSaved: () => void;
  /**
   * What the board makes of the draft: where it sits (`state`), and whether it collides or leaves the
   * shift. Only the board has the rows to know, and the page's bar needs it; `null` when there is no
   * draft, and on unmount, which is how the page learns the board is gone. The technician's name
   * rides along for the bar's summary line.
   */
  onDraftChange: (draft: PlanningDraft | null, technicianName: string | null) => void;
  /**
   * False while the page is showing the week and keeping this board mounted only so a docked form
   * survives — the form lives here. The board then neither follows the week's day with the form nor
   * scrolls anything: its container is hidden and measures nothing. Defaults to true.
   */
  active?: boolean;
  /**
   * The Gewerk of the Auftrag being planned (`usePlanningGewerkId`, called by the page so the week
   * agrees). Handwerker covering it are listed first, the rest are greyed out and stay plannable.
   * `null` leaves the board as it is.
   */
  matchGewerkId?: number | null;
  /**
   * The Leitung's edit mode for the default order of the Handwerker (owned by the page so it
   * outlives Tag ↔ Woche). Only the default board uses it — while planning, `orderByTrade` decides.
   */
  orderEditor: CalendarOrderEditor;
}

/**
 * The disposition board: one row per technician, time running horizontally.
 *
 * Click a block to edit it, drag it to move or resize. All of that runs through one drag primitive
 * with per-kind policies (`useBlockDrag` + `dragPolicy`), rather than the three parallel drag
 * implementations the deleted timeline had.
 */
export const DispositionBoardWidget = ({
  day,
  plan,
  source,
  planning,
  onPlanSaved,
  onDraftChange,
  active = true,
  matchGewerkId = null,
  orderEditor,
}: Props) => {
  // The entry being moved is dropped from the rows, so the draft that stands in its place has the
  // slot to itself.
  const { rows, isLoading, isError, refetch } = useDispositionBoard(
    day.dayIso,
    day.from,
    day.to,
    source && { kind: source.kind, ownerId: source.ownerId },
  );

  const navigate = useNavigate();

  const gewerkLabels = useGewerkLabels();
  const calendarRows = useCalendarRows(rows, orderEditor);

  const user = useAuthStore((state) => state.user);
  const dragContext = useMemo<DragContext>(() => ({ userRoles: user?.roles ?? [] }), [user?.roles]);

  const updateEntry = useUpdateScheduleEntry();
  const upsertBreak = useUpsertBreak();

  // The same three-dialog flow the upsert modals use. Until now the drag path had none of it:
  // a rejected drop just snapped back with no explanation (widget README §6).
  const conflictFlow = useScheduleConflictFlow();

  const appointmentModal = useUpsertAppointment();
  const taskModal = useUpsertTask();

  /** A form is docked beside the board. It, not the board, is what is being edited. */
  const modalOpen = appointmentModal.isOpen || taskModal.isOpen;

  /**
   * Two orderings, never mixed. Planning and editing a Termin list every Handwerker by what fits the
   * Auftrag (`orderByTrade`, over the plain user-id order so the saved calendar order and the hidden
   * flag play no part). Everything else shows the Leitung's order (`orderByCalendar`).
   */
  const planningRoster = plan.isPlanning || modalOpen;
  const editingOrder = orderEditor.isEditing && !planningRoster;

  const orderedRows = useMemo(
    () =>
      planningRoster
        ? orderByTrade(
            [...rows].sort((a, b) => a.technicianId - b.technicianId),
            matchGewerkId,
          )
        : calendarRows.ordered,
    [planningRoster, rows, matchGewerkId, calendarRows.ordered],
  );

  // ── Live slot ────────────────────────────────────────────────────────────────────────────────

  /**
   * What the open form currently says about *when* — the **single owner of the block being edited**
   * while a form is open, for both directions at once. The modal reports every keystroke into it,
   * and a drag on the board writes back into it instead of sending a request; the calendar draws it
   * and the modal reads it back. Both writes compare by value, so the value handed back to the form
   * it came from stops at the first comparison rather than looping.
   *
   * Dropped when the form closes, which is what makes closing without saving a discard: the board
   * falls back to what it drew before — the planning draft, or the entry the server has.
   */
  const [formSlot, setFormSlot] = useState<ScheduleSlot | null>(null);

  /**
   * Whether the open form says Ganztägig. Kept apart from `formSlot` on purpose — see
   * `UpsertAppointmentModal`'s `onFullDayChange`: anything inside `ScheduleSlot` is compared by
   * `sameSlot` and can therefore make the board write back over the form.
   */
  const [formIsFullDay, setFormIsFullDay] = useState(false);

  /**
   * `null` is never written from here. Both modals stay mounted and the closed one reports `null`
   * continuously, which would otherwise wipe the open one's slot; clearing is the effect below's
   * job, keyed on the thing that actually decides it.
   */
  const handleSlotChange = useCallback((next: ScheduleSlot | null) => {
    if (!next) return;
    setFormSlot((current) => (sameSlot(current, next) ? current : next));
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      setFormSlot(null);
      setFormIsFullDay(false);
    }
  }, [modalOpen]);

  /**
   * The day of the form and the day of the board are kept together, and **whichever one moved last
   * wins** — which is why the two directions below each react to their own change only.
   *
   * form → board: a Termin typed onto Thursday is drawn on Thursday rather than silently dropped, and
   * the form keeps everything that was typed — it is never reset by this. Triggered by the form's day
   * *changing*, not by the board's: with `day.dayIso` in the trigger, the arrows set `?day=` and this
   * effect sent the board straight back to the form's day, so the day could not be switched at all.
   *
   * board → form: switching the day with a form docked takes the Termin along — same clock time on
   * the new day, written through `handleSlotChange` like a drag is. Nothing is saved by this.
   *
   * `useDispositionDay` rebuilds its callbacks every render, so `goToDay` is read through a ref: in
   * the dependency array it would re-run this on every render of the board.
   *
   * Idle while the week is on screen (`active`): the week's arrows move the anchor day by a week at
   * a time, and the form's Termin must not go with them. It catches up once the board is back —
   * `prevBoardDayRef` still holds the day it last saw, so a day chosen in the week arrives as the
   * board's day change.
   */
  const goToDayRef = useRef(day.goToDay);
  useEffect(() => {
    goToDayRef.current = day.goToDay;
  });

  const formDay = formSlot ? slotDayIso(formSlot) : null;
  const followedFormDayRef = useRef<string | null>(null);
  const prevBoardDayRef = useRef(day.dayIso);

  useEffect(() => {
    if (!active) return;

    const boardDayChanged = prevBoardDayRef.current !== day.dayIso;
    prevBoardDayRef.current = day.dayIso;

    if (!formSlot || !formDay) {
      followedFormDayRef.current = null;
      return;
    }

    // board → form. The arrows moved the board; the form's Termin follows it.
    if (boardDayChanged && formDay !== day.dayIso) {
      followedFormDayRef.current = day.dayIso;
      handleSlotChange(withSlotDay(formSlot, day.dayIso));
      return;
    }

    // form → board. Only a day the form has newly arrived on.
    if (followedFormDayRef.current === formDay) return;
    followedFormDayRef.current = formDay;
    if (formDay !== day.dayIso) goToDayRef.current(formDay);
  }, [active, formSlot, formDay, day.dayIso, handleSlotChange]);

  /**
   * The form's slot as a block on this board. `null` while a field is half-typed, or while the slot
   * belongs to another day — the effect above is on its way there.
   */
  const liveDraft = useMemo(
    () => (formSlot ? slotToDraft(formSlot, day.dayIso) : null),
    [formSlot, day.dayIso],
  );

  /**
   * The existing entry the open form belongs to, when there is one.
   *
   * It keeps its place in the lane packing — dropping it would make the whole row jump the moment a
   * form opened — and is dimmed instead, with the live block drawn over it. The collision check
   * skips its key, so the block cannot report a conflict with itself.
   */
  const editedBlock = useMemo(() => {
    const appointmentId = appointmentModal.isOpen ? appointmentModal.appointmentId : null;
    const taskId = taskModal.isOpen ? taskModal.taskId : null;
    if (appointmentId == null && taskId == null) return null;

    return (
      rows
        .flatMap((row) => row.blocks)
        .find((block) =>
          appointmentId != null
            ? block.entry.appointmentId === appointmentId
            : block.entry.taskId === taskId,
        ) ?? null
    );
  }, [
    rows,
    appointmentModal.isOpen,
    appointmentModal.appointmentId,
    taskModal.isOpen,
    taskModal.taskId,
  ]);

  /**
   * Colours for the live block: the edited entry's own, so a started Termin keeps looking started,
   * and otherwise the mode's — a block being created has no status yet.
   */
  const liveColors = useMemo(() => {
    if (editedBlock) return getBlockColors(editedBlock.kind, editedBlock.status);
    const kind = taskModal.isOpen || plan.mode === 'schedule-task' ? 'TASK' : 'APPOINTMENT';
    return getBlockColors(kind, null);
  }, [editedBlock, taskModal.isOpen, plan.mode]);

  /**
   * What the live block is allowed to collide with.
   *
   * A Ganztägig Termin spans the technician's whole shift, so it always crosses the Pause — and the
   * API does not call that a conflict: `ScheduleConflictChecker.ignoreBreaks` drops projected breaks
   * from the overlap query whenever `allDay` is set. Without the same skip here the block would sit
   * permanently red and `PlanningBar` would announce an Überschneidung that can never be raised.
   *
   * The same relationship as `isOutsideShift` and `findWorkingHoursViolations`: an advisory that
   * mirrors a server rule, and has to mirror the exceptions too.
   */
  const draftCollisionBlocks = useCallback(
    (blocks: BoardBlock[]) =>
      formIsFullDay ? blocks.filter((block) => block.kind !== 'BREAK') : blocks,
    [formIsFullDay],
  );

  /**
   * Sends one finished drag, and re-sends it with an override when the user confirms a 409.
   *
   * `block` and `outcome` are parameters rather than reads off `drag`, because the retry runs from a
   * dialog button — the pointer gesture is long over by then and `drag` is back to `null`.
   *
   * `mutateAsync`, not `mutate`: neither mutation has an `onError` that could route the rejection
   * out (the update hook is deliberately non-optimistic, so there is nothing to roll back), and
   * `mutate` would swallow it into `isError` where nothing reads it. That silence is what made an
   * off-shift drop look like it simply did not take.
   */
  const commitDrag = useCallback(
    async (block: BoardBlock, outcome: DragOutcome, overrides: ScheduleOverrides = {}) => {
      const patch = buildSchedulePatch(
        block,
        resolveDragPolicy(block, dragContext),
        outcome,
        overrides,
      );
      // A no-op drag — same slot, same technician. Nothing to send.
      if (!patch) return;

      try {
        if (patch.kind === 'break') await upsertBreak.mutateAsync(patch.dto);
        else await updateEntry.mutateAsync({ entryId: patch.entryId, dto: patch.dto });
      } catch (error) {
        // Pass back the overrides this attempt actually used: the API checks working hours before
        // overlaps, so one drop can 409 twice and the second confirmation must keep the first flag.
        conflictFlow.handle(error, {
          overrides,
          attempt: (next) => commitDrag(block, outcome, next),
        });
      }
    },
    [conflictFlow, dragContext, updateEntry, upsertBreak],
  );

  const handleCommit = useCallback(
    (subject: DragSubject, outcome: DragOutcome) => {
      // The draft is not on the server — a finished gesture just moves the local state.
      if (subject.type === 'draft') {
        const handle = outcome.handle as 'move' | SegmentHandle;

        // A form owns the block: the gesture retypes its four scheduling fields and nothing is sent.
        // The modal's submit stays the only thing that writes — dragging must not save behind a
        // half-filled form's back.
        if (liveDraft) {
          const next = applyDraftDrag(liveDraft, handle, outcome.deltaMinutes, outcome.technicianId);
          handleSlotChange(draftToDefaultSlot(next, day.dayIso));
          return;
        }

        planning.applyDrag(handle, outcome.deltaMinutes, outcome.technicianId);
        return;
      }

      void commitDrag(subject.block, outcome);
    },
    [commitDrag, planning, liveDraft, handleSlotChange, day.dayIso],
  );

  const { drag, startDrag, wasDragged } = useBlockDrag(handleCommit);

  const onDragStart = useCallback(
    (block: BoardBlock, handle: DragHandle, e: React.PointerEvent) => {
      const policy = resolveDragPolicy(block, dragContext);
      if (handle === 'move' ? !policy.canMove : !policy.handles.includes(handle)) return;
      startDrag(blockSubject(block), handle, policy.canReassign, block.entry.technicianId, e);
    },
    [dragContext, startDrag],
  );

  /** The draft's implicit policy: freely movable, reassignable, all three segment handles. */
  const onDraftDragStart = useCallback(
    (handle: 'move' | SegmentHandle, e: React.PointerEvent) => {
      const placed = liveDraft ?? planning.draft;
      if (!placed) return;
      startDrag(draftSubject(placed), handle, true, placed.technicianId, e);
    },
    [liveDraft, planning.draft, startDrag],
  );

  const preview = useMemo<DragPreview | null>(() => {
    if (!drag) return null;

    // The draft mid-drag: same ghost, same collision check, layout from the same one drag rule.
    if (drag.blockKey === DRAFT_KEY) {
      const placed = liveDraft ?? planning.draft;
      if (!placed) return null;

      const handle = drag.handle as 'move' | SegmentHandle;
      const next = applyDraftDrag(placed, handle, drag.deltaMinutes, drag.technicianId);
      const layout = draftLayout(next, liveColors, handle);
      const targetRow = rows.find((row) => row.technicianId === drag.technicianId);

      return {
        blockKey: DRAFT_KEY,
        technicianId: drag.technicianId,
        layout,
        lane: 0,
        // The entry the form is editing is dimmed and stood in for — it must not flag its own slot.
        colliding: targetRow
          ? detectDragCollision(
              draftCollisionBlocks(targetRow.blocks),
              layout,
              editedBlock?.key ?? DRAFT_KEY,
            ).length > 0
          : false,
        outsideShift: targetRow ? isOutsideShift(layout, targetRow.shift) : false,
      };
    }

    const block = rows.flatMap((row) => row.blocks).find((b) => b.key === drag.blockKey);
    if (!block) return null;

    const layout = buildPreviewLayout(block, day.dayIso, drag.handle, drag.deltaMinutes);
    const targetRow = rows.find((row) => row.technicianId === drag.technicianId);
    // `PreviewLayout` is structurally a `MinuteRange`, so the collision check needs no adapter.
    const colliding = targetRow
      ? detectDragCollision(targetRow.blocks, layout, block.key).length > 0
      : false;

    return {
      blockKey: block.key,
      technicianId: drag.technicianId,
      layout,
      // The block's own lane is meaningless in another row; drop it on the first one.
      lane: drag.technicianId === drag.originTechnicianId ? block.lane : 0,
      colliding,
      // The shift of the row the pointer is over, not the block's own — moving a Termin to a
      // technician who is off at that hour is exactly the case this warns about.
      outsideShift: targetRow ? isOutsideShift(layout, targetRow.shift) : false,
    };
  }, [
    drag,
    rows,
    day.dayIso,
    liveDraft,
    planning.draft,
    liveColors,
    editedBlock,
    draftCollisionBlocks,
  ]);

  // ── Planning mode ────────────────────────────────────────────────────────────────────────────

  const draft = useMemo<PlanningDraft | null>(() => {
    // The open form wins: it is what the user is looking at, and the draft underneath is only where
    // things stood before it opened. Outside planning mode the live block is the only one there is —
    // that is the whole of the "edit an existing Termin and watch it move" case.
    const placed = liveDraft ?? (plan.isPlanning ? planning.draft : null);
    if (!placed) return null;

    const layout = draftLayout(placed, liveColors);
    const targetRow = rows.find((row) => row.technicianId === placed.technicianId);

    return {
      technicianId: placed.technicianId,
      layout,
      // Nothing to exclude for a fresh draft — it is not on the board yet. For an edit it is the
      // entry standing behind the live block, which must not be reported as its own conflict.
      colliding: targetRow
        ? detectDragCollision(
            draftCollisionBlocks(targetRow.blocks),
            layout,
            editedBlock?.key ?? '',
          ).length > 0
        : false,
      // Covers the initial pick too: a click in the grey zone lands a draft that is already flagged,
      // without a drag having happened.
      outsideShift: targetRow ? isOutsideShift(layout, targetRow.shift) : false,
      state: placed,
    };
  }, [
    liveDraft,
    plan.isPlanning,
    planning.draft,
    liveColors,
    editedBlock,
    rows,
    draftCollisionBlocks,
  ]);

  /** The picked slot, as the modals want it. `undefined` before anything is picked. */
  const plannedSlot = useMemo(
    () => (planning.draft ? draftToDefaultSlot(planning.draft, day.dayIso) : undefined),
    [planning.draft, day.dayIso],
  );

  /**
   * What each modal is handed: the live slot once it is open — that is how a drag reaches the form —
   * and the picked slot before that, which is what seeds the form when it opens.
   *
   * Gated on the modal being open (or its mode being active) so the other modal, which stays mounted
   * and closed, is never handed a slot belonging to the one in use.
   */
  const appointmentSlot =
    appointmentModal.isOpen || plan.mode === 'schedule-appointment'
      ? formSlot ?? plannedSlot ?? undefined
      : undefined;

  const taskSlot =
    taskModal.isOpen || plan.mode === 'schedule-task'
      ? formSlot ?? plannedSlot ?? undefined
      : undefined;

  /**
   * Tell the page what the board makes of the draft. The bar and the save button live there, and
   * the save has to book what is *drawn* — with a form docked that is the form's slot, not the
   * picked one — and the two advisories need the rows only this widget has.
   *
   * `null` on the way out is what lets the page stop trusting stale advisories the moment the board
   * is replaced by the week.
   */
  const draftTechnicianName =
    rows.find((row) => row.technicianId === draft?.technicianId)?.technicianName ?? null;

  useEffect(() => {
    onDraftChange(draft, draftTechnicianName);
  }, [draft, draftTechnicianName, onDraftChange]);

  useEffect(() => () => onDraftChange(null, null), [onDraftChange]);

  /**
   * Bring the seeded draft into view, once per arrival.
   *
   * The board is 3104px wide across sixteen hours, so a Termin at 09:00 in the fifth Handwerker's
   * row can be off-screen on both axes — being sent to the right day is no use if you then have to
   * hunt for it. Rect maths against the container rather than `toXPx`, so the sticky technician
   * column and the lane stack need no re-derivation; not `scrollIntoView`, which would also scroll
   * ancestors and push the sticky header out of the viewport.
   *
   * `planning.draft` is in the deps and not decoration: the seed lands in `usePlanningDraft`'s state
   * during the same commit this effect first runs in, so on that pass the element does not exist
   * yet. Bailing without setting the ref lets the next render — the one that actually mounts the
   * draft — do the scroll.
   *
   * The ref key means this fires on arrival and on a jump back to the source day, and never fights
   * the dispatcher's own scrolling afterwards.
   */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didFocusRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active || !source || source.dayIso !== day.dayIso) return;

    const key = `${source.kind}:${source.ownerId}:${day.dayIso}`;
    if (didFocusRef.current === key) return;

    const container = scrollRef.current;
    const block = container?.querySelector<HTMLElement>('[data-planning-draft]');
    if (!container || !block) return;

    const containerBox = container.getBoundingClientRect();
    const blockBox = block.getBoundingClientRect();

    // Enough lead-in to show the slot in context rather than flush against the technician column.
    container.scrollLeft += blockBox.left - containerBox.left - TECHNICIAN_COL_WIDTH - 80;
    container.scrollTop += blockBox.top - containerBox.top - 80;

    didFocusRef.current = key;
  }, [active, source, day.dayIso, rows, planning.draft]);

  /**
   * Keep the block the form is editing out from under the docked panel.
   *
   * Keyed on the row and the day, not on the slot: scrolling on every keystroke would fight the
   * dispatcher's own scrolling while they type. Moving the block to another Handwerker or another
   * day is the one case where it can leave the screen entirely, and then it is brought back.
   */
  const liveFocusKey =
    active && modalOpen && liveDraft ? `${liveDraft.technicianId}:${day.dayIso}` : null;
  const didFocusLiveRef = useRef<string | null>(null);

  useEffect(() => {
    if (!liveFocusKey) {
      didFocusLiveRef.current = null;
      return;
    }
    if (didFocusLiveRef.current === liveFocusKey) return;

    const container = scrollRef.current;
    const block = container?.querySelector<HTMLElement>('[data-planning-draft]');
    // The live block mounts one render after the slot arrives; leaving the ref unset lets that
    // render do the scrolling. Same reasoning as the seed effect above.
    if (!container || !block) return;

    const containerBox = container.getBoundingClientRect();
    const blockBox = block.getBoundingClientRect();

    // The usable strip is the container itself — the layout has already made room for the docked
    // panel beside it (`shared/lib/dockedPanel`) — minus the sticky technician column.
    const visibleRight = containerBox.right;
    const visibleLeft = containerBox.left + TECHNICIAN_COL_WIDTH;

    if (blockBox.right > visibleRight) container.scrollLeft += blockBox.right - visibleRight + 40;
    else if (blockBox.left < visibleLeft) container.scrollLeft += blockBox.left - visibleLeft - 40;

    if (blockBox.top < containerBox.top || blockBox.bottom > containerBox.bottom) {
      container.scrollTop += blockBox.top - containerBox.top - 80;
    }

    didFocusLiveRef.current = liveFocusKey;
  }, [liveFocusKey, rows]);

  /**
   * Land on the Handwerker the week view was pointing at.
   *
   * Arriving from a week cell you already know whose day you asked for; with a dozen rows on a
   * board that scrolls, the answer is often below the fold. Vertical only — the week says nothing
   * about a time, so moving the hour axis would be inventing an intent.
   *
   * The param is cleared once consumed, so it is a one-shot instruction and not a piece of state
   * that would re-scroll on every later render.
   */
  const [focusTechnician, setFocusTechnician] = useQueryState(
    FOCUS_TECHNICIAN_PARAM,
    parseAsInteger,
  );
  const didFocusTechnicianRef = useRef<number | null>(null);

  useEffect(() => {
    if (focusTechnician == null) {
      didFocusTechnicianRef.current = null;
      return;
    }
    if (didFocusTechnicianRef.current === focusTechnician) return;

    const container = scrollRef.current;
    // The rows mount a render after the query resolves; bailing without setting the ref lets that
    // render do the scrolling. Same reasoning as the two effects above.
    const track = container?.querySelector<HTMLElement>(
      `[${ROW_TECHNICIAN_ATTR}="${focusTechnician}"]`,
    );
    if (!container || !track) return;

    const containerBox = container.getBoundingClientRect();
    const trackBox = track.getBoundingClientRect();
    // Clear of the sticky hour axis rather than flush under it.
    container.scrollTop += trackBox.top - containerBox.top - 80;

    didFocusTechnicianRef.current = focusTechnician;
    void setFocusTechnician(null);
  }, [focusTechnician, rows, setFocusTechnician]);

  return (
    // Inactive: only the grid goes. The docked forms are drawn from inside this tree, so hiding the
    // root would hide the very form the board is being kept alive for.
    <div className={active ? 'flex h-full flex-col' : 'contents'}>
      <div
        ref={scrollRef}
        className={`relative min-h-0 flex-1 overflow-auto bg-white ${active ? '' : 'hidden'}`}
      >
        <div style={{ width: TECHNICIAN_COL_WIDTH + BOARD_WIDTH }}>
          <div
            className={`sticky top-0 flex border-b border-slate-200 bg-white ${BOARD_LAYER.header}`}
          >
            <div
              className={`sticky left-0 flex shrink-0 items-center justify-between gap-2 bg-white px-3 py-3 text-base text-zinc-500 outline outline-1 outline-offset-[-0.50px] outline-slate-200 ${BOARD_LAYER.headerCorner}`}
              style={{ width: TECHNICIAN_COL_WIDTH }}
            >
              Handwerkern
              <CalendarOrderToolbar editor={orderEditor} disabled={planningRoster} />
            </div>
            <BoardTimeAxis />
          </div>

          {isLoading ? (
            <BoardSkeleton />
          ) : isError ? (
            <BoardMessage
              text="Der Kalender konnte nicht geladen werden."
              action={{ label: 'Erneut versuchen', onClick: () => refetch() }}
            />
          ) : rows.length === 0 ? (
            <BoardMessage text="Keine Handwerker gefunden." />
          ) : (
            <div className="relative">
              <CalendarOrderList
                ids={orderedRows.map(({ row }) => row.technicianId)}
                editing={editingOrder}
                onMove={calendarRows.onMove}
              >
              {orderedRows.map(({ row, muted }) => (
                <CalendarOrderRow
                  key={row.technicianId}
                  technicianId={row.technicianId}
                  editing={editingOrder}
                  hidden={muted}
                  onToggleHidden={calendarRows.onToggleHidden}
                >
                  {(controls) => (
                <BoardRow
                  row={row}
                  muted={muted}
                  actions={controls}
                  trades={technicianTrades(row.trades, gewerkLabels, matchGewerkId)}
                  dayIso={day.dayIso}
                  dragContext={dragContext}
                  preview={preview}
                  onDragStart={onDragStart}
                  wasDragged={wasDragged}
                  // A click on the track places a slot — but not while a form is open on top of it,
                  // where a click meant to bring the board into focus would teleport the block.
                  pickable={plan.isPlanning && !modalOpen}
                  blocksInert={plan.isPlanning || modalOpen || editingOrder}
                  dimmedKey={editedBlock?.key ?? null}
                  // Hover cards stay away for the length of a gesture, and stay clear of the docked
                  // panel — portaled to `body`, they can still reach under it, and they sit below
                  // it in the stacking order, so they move rather than lose.
                  dragActive={drag !== null}
                  reservedRightPx={modalOpen ? DOCKED_PANEL_WIDTH : 0}
                  draft={draft}
                  onPickSlot={planning.place}
                  onDraftDragStart={onDraftDragStart}
                />
                  )}
                </CalendarOrderRow>
              ))}
              </CalendarOrderList>
              {day.isToday && <BoardCurrentTimeLine offsetX={TECHNICIAN_COL_WIDTH} />}
            </div>
          )}
        </div>
      </div>

      {/*
        Siblings of the scroll container, not children of it — a modal nested inside
        `overflow-auto` would be clipped and would scroll with the grid.

        Mounted once for the whole board: every block only writes the URL and the modals read it,
        which is also what makes
        `/disposition?day=2026-08-11&upsert-appointment=edit&upsert-appointment-id=42` a working
        deep link.

        `placement="side"` docks them beside the board instead of over it, which is what makes the
        live preview worth having — the block and the fields that describe it are on screen together.
        Beside, literally: the panel reports its width to `shared/lib/dockedPanel` and `AppLayout`
        reserves it, so this scroller ends where the panel begins and nothing is drawn under it.
        `defaultSlot` and `onSlotChange` are the two halves of that loop; `formSlot` above owns the
        value both of them read and write.
      */}
      <UpsertAppointmentModal
        defaultSlot={appointmentSlot}
        onSlotChange={handleSlotChange}
        onFullDayChange={setFormIsFullDay}
        placement="side"
        onSuccess={onPlanSaved}
      />
      <UpsertTaskModal
        defaultSlot={taskSlot}
        onSlotChange={handleSlotChange}
        placement="side"
        onSuccess={onPlanSaved}
      />

      {/*
        Last in the DOM on purpose: every modal shares z-index 100, so a dialog raised by a drag has
        to come after the two upsert modals to sit above one that happens to be open.

        The wording says "verschieben" rather than the modals' "speichern" — this path is a drag,
        and the amber ghost has already said the same thing before the drop.
      */}
      <ScheduleConflictDialogs
        flow={conflictFlow}
        isLoading={updateEntry.isPending || upsertBreak.isPending}
        onGoToReschedule={() => navigate(routes.reschedule.root)}
        conflictTitle="Terminüberschneidung erkannt"
        conflictDescription="An dieser Zeit liegt bereits ein Eintrag. Möchten Sie den Block trotzdem verschieben?"
        conflictConfirmLabel="Trotzdem verschieben"
        workingHoursDescription="Der Techniker arbeitet zu dieser Zeit nicht. Möchten Sie den Block trotzdem verschieben?"
        blockersDescription="Die folgenden abgeschlossenen Einträge blockieren diesen Zeitraum."
      />
    </div>
  );
};
